import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { motion, radius, transition } from "../../theme/tokens";
import { authTokens as t } from "../../theme/authTokens";

/**
 * Field, button and label styling shared by the sign-in and account-recovery
 * cards. Kept here so the two screens cannot drift apart, and so neither of
 * them has to carry a wall of `sx` around the logic that matters.
 *
 * These are typed as `SystemStyleObject` rather than `SxProps`, which matters:
 * `SxProps` is a union that includes arrays and callbacks, and MUI's array form
 * (`sx={[authButtonSx, { mt: 3 }]}`) only accepts plain style objects as its
 * entries. Typing them as the union makes every array-merged call site fail to
 * match the Button overload.
 */
type AuthStyle = SystemStyleObject<Theme>;

/** Outlined input: 52px, 13px radius, brand focus ring. */
export const authFieldSx: AuthStyle = {
  "& .MuiOutlinedInput-root": {
    height: t.fieldHeight,
    borderRadius: `${radius.lg - 1}px`,
    backgroundColor: t.surface,
    color: t.text,
    transition: transition(["box-shadow", "border-color"], motion.fast),
    "& fieldset": { borderColor: t.border, transition: transition(["border-color"], motion.fast) },
    "&:hover fieldset": { borderColor: t.borderStrong },
    "&.Mui-focused fieldset": { borderColor: t.blue, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: `0 0 0 4px ${alpha(t.blue, 0.12)}` },
    "&.Mui-error fieldset": { borderColor: "#E11D48" },
    "&.Mui-error.Mui-focused": { boxShadow: `0 0 0 4px ${alpha("#E11D48", 0.12)}` },
  },
  "& .MuiInputBase-input": {
    fontSize: 14.5,
    color: t.text,
    "&::placeholder": { color: alpha(t.muted, 0.72), opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root fieldset": { transition: "none" },
  },
};

/** The small caps label that sits above each field. */
export const authLabelSx: AuthStyle = {
  display: "block",
  mb: 0.85,
  fontSize: 12.5,
  fontWeight: 600,
  color: t.text,
};

/** Primary call to action — GT blue gradient, 52px, full width. */
export const authButtonSx: AuthStyle = {
  height: t.ctaHeight,
  borderRadius: `${radius.lg - 1}px`,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: "0.005em",
  textTransform: "none",
  color: "#ffffff",
  backgroundImage: t.ctaGradient,
  backgroundColor: t.blue,
  boxShadow: t.ctaShadow,
  transition: transition(["background-image", "box-shadow", "transform"], motion.fast),
  "&:hover": {
    backgroundImage: t.ctaGradientHover,
    boxShadow: t.ctaShadowHover,
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)", boxShadow: t.ctaShadow },
  "&:focus-visible": { outline: `2px solid ${alpha(t.blue, 0.55)}`, outlineOffset: 2 },
  /* Two disabled looks. Incomplete form: a legible slate surface, because the
     MUI default is almost invisible on white. Mid-submit: the button keeps its
     brand fill so the inline loader stays readable. */
  "&.Mui-disabled": {
    backgroundImage: "none",
    backgroundColor: "#E3E9F2",
    color: "#8494A8",
    boxShadow: "none",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": { transform: "none" },
  },
};

/** Mid-submit override, merged over `authButtonSx` while a request is in flight. */
export const authButtonBusySx: AuthStyle = {
  "&.Mui-disabled": {
    backgroundImage: t.ctaGradient,
    backgroundColor: t.blue,
    color: "#ffffff",
    opacity: 0.86,
    boxShadow: "none",
  },
};
